import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';

/**
 * Column Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-column-graph',
  standalone: false,
  templateUrl: './column-graph.component.html',
  styleUrl: './column-graph.component.scss'
})
export class ColumnGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private columnGraphReportSubscription: any;
  private isColumnViewInitialized = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.columnGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Column Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isColumnViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isColumnViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    try {
      this.root = this._baseChartService.createRoot('column-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'column',
        data: data
      });

      const chart = this._baseChartService.createChartContainer(this.root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: this.root.verticalLayout
      });

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(chart, 'slide');

      this._baseChartService.setupColorPalette(chart);

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 50,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      // Enhanced axis animation
      this._baseChartService.setupAxisAnimation(categoryAxis);

      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y',
      });

      // Enhanced axis animation for value axis
      this._baseChartService.setupAxisAnimation(valueAxis);

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((_valueField_) => {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(this.root, {
            name: _valueField_,
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: _valueField_,
            categoryXField: categoryField
          })
        );
       
        this._baseChartService.setupSeriesAnimation(series, 'elastic');

        const tooltip = am5.Tooltip.new(this.root, {
          getFillFromSprite: true,
          labelText: `{name}\n{categoryX}: {valueY.formatNumber("#,##0")}`,
          pointerOrientation: 'horizontal'
        });
        tooltip.label.set('fill', am5.color(0xFFFFFF));
        this._baseChartService.setupTooltipAnimation(tooltip);
        series.set('tooltip', tooltip);
       
        series.data.setAll(data);
       
        // Set chart padding at top so labels don't get clipped
          // Add padding to prevent labels from being cut off
          chart.set("paddingTop", 40);
          
          // Add value labels at the top of each bar
          series.bullets.push(() =>
            am5.Bullet.new(this.root, {
              locationY: 0,
              sprite: am5.Label.new(this.root, {
                text: "{valueY}",
                centerX: am5.p50,
                centerY: am5.p100,
                dy: -10,                         // Position label above the bar
                populateText: true,
                fontSize: 13,
                fill: am5.color(0x000000),       // Black text
                oversizedBehavior: "wrap"        // Prevent clipping or shrinking
              })
            })
          );
      });

      // Add cursor
      chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: categoryAxis
      }));

      // Add legend
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      this._baseChartService.setupLegendAnimation(legend);

      legend.set('maxHeight', 40);

      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      legend.data.setAll(chart.series.values);

      // Trigger enhanced chart animation
      setTimeout(() => {
        chart.appear(1200, 100);
      }, 100);

    } catch (error) {
      console.error('Error creating column chart:', error);
    }
  }

  private updateChart(data: BarChartData[]) {
    // Update the stored data
    this.data = data;
    
    // Dispose existing chart first
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a small delay to ensure proper disposal
    setTimeout(() => {
      this.createChart(data);
      
      // Update the chart data in the registry
      BaseChartService.updateActiveChartData(data);
    }, 150);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    this.columnGraphReportSubscription.unsubscribe();
  }
}
